import {GIFT_CARD_CODE_LENGTH, NAV_OWNER} from '@/constants';
import {GiftCardRedeem} from '@/types';
import {isSameOrigin} from './page.utils';

export const normalizeGiftCardCode = (codeString: string): string | null => {
	return typeof codeString === 'string'
		? codeString
				.toUpperCase()
				.replace(/[^A-Z0-9]/gi, '')
				.substring(0, GIFT_CARD_CODE_LENGTH)
		: null;
};

const getPricingCurrencyLocale = (locale: string): string => {
	switch (locale) {
		case 'pt':
			return 'pt-PT'; // Portuguese (European)
		default:
			return locale; // Return the original locale if no specific mapping is needed
	}
};

const getNumberingSystemForLocale = (locale: string) => {
	switch (locale) {
		case 'ar':
			return 'latn'; // "latn" currency, as ar(dirham) wallet does not exist in Epic, so arabic numerals are not warranted for currency.
		default:
			return undefined;
	}
};

/**
 * Formats currency with respects to the input currency code (ISO 4217) and override options. If the currency has no decimal
 * parts, or the price is an even number (e.g. $15) the preference unless overridden in provided options here is to
 * set the min/max decimal settings to 0
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl
 */

const NO_DECIMALS: Partial<Intl.NumberFormatOptions> = {
	maximumFractionDigits: 0,
	minimumFractionDigits: 0
};

export const formatCurrency = (
	amountInCents: number,
	currency: string,
	locale: string,
	decimals: number
) => {
	const factor = Math.pow(10, decimals);
	const amount = amountInCents / factor;
	const decimalOptions =
		decimals === 0
			? NO_DECIMALS
			: {
					maximumFractionDigits: decimals
				};
	const optionsWithCurrency: Partial<Intl.NumberFormatOptions> = {
		currency,
		style: 'currency',
		...decimalOptions
	};
	const correctLocale = getPricingCurrencyLocale(locale);
	const numberingSystem = getNumberingSystemForLocale(correctLocale);
	const newOptions: Partial<Intl.NumberFormatOptions> = {
		localeMatcher: 'best fit',
		maximumFractionDigits: 20,
		numberingSystem,
		...(optionsWithCurrency || {})
	};
	const formatted = new Intl.NumberFormat(correctLocale, newOptions).format(amount); // variable for debugging purposes.
	return formatted;
};

export const hasExceededLimit = (giftCard: GiftCardRedeem) => {
	if (typeof giftCard?.walletDetails !== 'object') return false;
	const {amount, walletDetails} = giftCard;
	const newBalance = amount + walletDetails.balanceBefore;
	return newBalance > walletDetails.maxAllowedBalance;
};

export const getRedeemSuccessRedirectUrl = (redirectUrl: string) => {
	try {
		const returnTo = new URL(redirectUrl);

		// 1 = redeemed, 0 = failed
		returnTo.searchParams.set('r', '1');
		returnTo.searchParams.set('source', NAV_OWNER);

		return isSameOrigin(redirectUrl) ? returnTo.toString() : undefined;
	} catch {
		return undefined;
	}
};

export const getGiftCardRedirectUrl = (code: string): string => {
	try {
		const redirectUrl = new URL(
			`${process.env.NEXT_PUBLIC_EPIC_SERVER_URL}${code ? `/${code}` : ''}?${location.search}${location.hash}`
		);
		redirectUrl.searchParams.set('autoRedeem', 'true'); // Set autoRedeem here to true.
		return redirectUrl.toString();
	} catch {
		return `${process.env.NEXT_PUBLIC_EPIC_SERVER_URL}${code ? `/${code}` : ''}?autoRedeem=true`;
	}
};

/**
 * Localizes and formats number, not currency related
 */
export const formatNumber = (value: number, locale: string) => {
	return new Intl.NumberFormat(locale || 'en-US', {
		style: 'decimal',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
		useGrouping: true, // thousands separators
		numberingSystem: getNumberingSystemForLocale(locale)
	}).format(value);
};

export const isCodeVerified = (data: GiftCardRedeem): boolean => {
	return !!data?.epicAccountId && !!data?.pin;
};
